import { Group, Badge, Text } from '@repletoryreach/sdk-ui';
import type { AuditRow } from '../../../data/useAuditFeed';
import { TenantName } from '../../../components/TenantName';
import { TwoLineCell } from '../../../components/TwoLineCell';
import { formatDateTime, formatRelative } from '../../../lib/format';
import { useT } from '../../../i18n/I18nProvider';
import { SOURCE_ACCENT, SOURCE_COLOR, SOURCE_LABEL_KEY } from './sourceColors';
import './audit-timeline.css';

const Chevron = (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M6 3.5L10.5 8L6 12.5" />
  </svg>
);

/**
 * One event in the timeline view.
 *
 * The row is a `<li>` with a real `<time>`, because the list is the default
 * view here and a wall of `div`s gives a screen reader nothing to navigate.
 * Below the `sm` breakpoint the fixed timestamp and tenant columns would crush
 * the summary into a sliver, so the row wraps instead of clipping.
 */
export function TimelineRow({ row, onOpen }: { row: AuditRow; onOpen: () => void }) {
  const t = useT();
  const exact = formatDateTime(row.occurred_at);

  return (
    <li
      className="rr-audit-row rr-audit-row--accented"
      style={{ ['--rr-audit-accent' as string]: SOURCE_ACCENT[row.source] }}
    >
      <button type="button" className="rr-audit-row__button" onClick={onOpen} aria-label={t('audit.detail.open')}>
        <Group gap="sm" align="center" wrap="wrap" style={{ flex: 1, minWidth: 0 }}>
          <div style={{ width: 120, flexShrink: 0 }}>
            <time dateTime={row.occurred_at} title={exact}>
              <TwoLineCell primary={formatRelative(row.occurred_at)} secondary={exact} />
            </time>
          </div>
          <Badge size="xs" color={SOURCE_COLOR[row.source]} variant="light" style={{ flexShrink: 0 }}>
            {t(SOURCE_LABEL_KEY[row.source])}
          </Badge>
          <div style={{ width: 170, flexShrink: 0, minWidth: 0 }}>
            <TenantName id={row.organisation_id} subtle />
          </div>
          {/* Summary on top, the qualifier under it: the pattern that lets the
              feed stay dense without a column per attribute. */}
          <div style={{ flex: 1, minWidth: 200 }}>
            <TwoLineCell primary={row.summary} secondary={row.detail} />
          </div>
        </Group>
        <span className="rr-audit-row__chevron">{Chevron}</span>
      </button>
    </li>
  );
}
